import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../context/auth-provider';
import { can } from '../../../../auth/permissions';
import { IndicatorGroup } from '../../../../motion';
import { Tabs, TabIndicator, TabLabel } from '../../account/account.styled';
import { layout, palette, radius, shadow, spacing, transition, typography } from '../../../../style-utils/theme';
import { listingSectionHref, STAYS_LISTING_TABS, StaysListingTab } from './stays-listing-routes';

/**
 * THE LISTING'S TAB STRIP (plan S4) — the account page's pattern (one moving
 * pill, IndicatorGroup + TabIndicator) with plain Links, like StaysSectionTabs:
 * NavLink would mark Overview active on every deeper path, and the active tab
 * here is the section the router matched, handed down.
 *
 * A tab renders only when the viewer holds its permission (the append
 * contract in stays-listing-routes.ts). The New stay page is not a tab, so on
 * it no tab is current.
 */

const ListingTab = styled(Link)`
  position: relative;
  isolation: isolate;
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: ${layout.touchTarget};
  box-sizing: border-box;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
  text-align: center;
  text-decoration: none;
  transition: ${transition.colors};

  &:hover {
    color: ${palette.ink};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &[aria-current='page'] {
    color: ${palette.ink};
  }
`;

export interface StaysListingTabsProps {
  listingId: string;
  /** The section on screen; a key no tab has (the New stay page) marks none. */
  active: string;
  tabs?: StaysListingTab[];
}

export const StaysListingTabs: React.FC<StaysListingTabsProps> = ({ listingId, active, tabs = STAYS_LISTING_TABS }) => {
  const { permissions } = useAuth();
  const visible = tabs.filter(t => can(permissions, t.permission));
  if (!visible.length) return null;

  return (
    <Tabs aria-label="Listing sections">
      <IndicatorGroup id="stays-listing-tabs">
        {visible.map(t => {
          const current = t.key === active;

          return (
            <ListingTab key={t.key} to={listingSectionHref(listingId, t.key)} aria-current={current ? 'page' : undefined}>
              {current ? <TabIndicator id="stays-listing-tabs" data-active-indicator="true" /> : null}
              <TabLabel>{t.label}</TabLabel>
            </ListingTab>
          );
        })}
      </IndicatorGroup>
    </Tabs>
  );
};
